/**
 * NepaliDateConverter - Handles conversion between Nepali (BS) and English (AD) dates
 * Based on the algorithm from NepaliFunctions library
 */

class NepaliDateConverter {
  constructor() {
    this.bsMonthDays = this._getBsMonthDays();
    this.minBsYear = 1970;
    this.maxBsYear = 2100;
    this.baseAdDate = { year: 1944, month: 1, day: 1 };
    this.baseBsDate = { year: 2000, month: 9, day: 17 };
  }

  /**
   * Convert AD (English) date to BS (Nepali) date
   * @param {Object} adDate - { year, month, day }
   * @returns {Object} - { year, month, day }
   */
  adToBs(adDate) {
    this._validateAdDate(adDate);

    const daysDiff = this._countAdDays(this.baseAdDate, adDate) + 1;

    let bsYear = this.baseBsDate.year;
    let bsMonth = this.baseBsDate.month;
    let bsDay = this.baseBsDate.day;

    let daysRemaining = daysDiff;

    while (daysRemaining > 0) {
      const daysInMonth = this.bsMonthDays[bsYear]?.[bsMonth - 1];

      if (!daysInMonth) {
        throw new Error(
          `Missing BS calendar data for year ${bsYear}, month ${bsMonth}`
        );
      }

      const remainingDaysInCurrentMonth = daysInMonth - bsDay;

      if (daysRemaining > remainingDaysInCurrentMonth) {
        daysRemaining -= remainingDaysInCurrentMonth + 1;
        bsDay = 1;
        bsMonth++;

        if (bsMonth > 12) {
          bsMonth = 1;
          bsYear++;

          if (bsYear > this.maxBsYear) {
            throw new Error("Converted BS date exceeds supported range.");
          }
        }
      } else {
        bsDay += daysRemaining;
        daysRemaining = 0;
      }
    }

    return { year: bsYear, month: bsMonth, day: bsDay };
  }

  /**
   * Convert BS (Nepali) date to AD (English) date
   * @param {Object} bsDate - { year, month, day }
   * @returns {Object} - { year, month, day }
   */
  bsToAd(bsDate) {
    this._validateBsDate(bsDate);

    const daysDiff = this._countBsDays(this.baseBsDate, bsDate);
    return this._addAdDays(this.baseAdDate, daysDiff);
  }

  /**
   * Count days between two AD dates
   * @private
   */
  _countAdDays(fromDate, toDate) {
    const from = new Date(fromDate.year, fromDate.month - 1, fromDate.day);
    const to = new Date(toDate.year, toDate.month - 1, toDate.day);
    return Math.floor((to - from) / (1000 * 60 * 60 * 24));
  }

  /**
   * Count days between two BS dates
   * @private
   */
  _countBsDays(fromDate, toDate) {
    let days = 0;

    // Same year case
    if (fromDate.year === toDate.year) {
      if (fromDate.month === toDate.month) {
        return toDate.day - fromDate.day;
      }

      // Days remaining in month of fromDate
      days +=
        this.bsMonthDays[fromDate.year][fromDate.month - 1] - fromDate.day + 1;

      // Days in complete months between fromDate and toDate
      for (let month = fromDate.month + 1; month < toDate.month; month++) {
        days += this.bsMonthDays[fromDate.year][month - 1];
      }

      // Days in the month of toDate
      days += toDate.day - 1;

      return days;
    }

    // Different years case

    // Days remaining in year of fromDate
    days +=
      this.bsMonthDays[fromDate.year][fromDate.month - 1] - fromDate.day + 1;
    for (let month = fromDate.month + 1; month <= 12; month++) {
      days += this.bsMonthDays[fromDate.year][month - 1];
    }

    // Days in complete years between fromDate and toDate
    for (let year = fromDate.year + 1; year < toDate.year; year++) {
      days += this._getYearDays(year);
    }

    // Days in the year of toDate
    for (let month = 1; month < toDate.month; month++) {
      days += this.bsMonthDays[toDate.year][month - 1];
    }
    days += toDate.day - 1;

    return days;
  }

  /**
   * Add days to an AD date
   * @private
   */
  _addAdDays(adDate, days) {
    const date = new Date(adDate.year, adDate.month - 1, adDate.day);
    date.setDate(date.getDate() + days);

    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
    };
  }

  /**
   * Get total days in a BS year
   * @private
   */
  _getYearDays(year) {
    if (!this.bsMonthDays[year]) {
      throw new Error(`BS year ${year} is out of range`);
    }
    return this.bsMonthDays[year].reduce((sum, days) => sum + days, 0);
  }

  /**
   * Validate AD date
   * @private
   */
  _validateAdDate(date) {
    if (!date || typeof date !== "object") {
      throw new Error("Invalid AD date format");
    }

    const { year, month, day } = date;

    if (
      !Number.isInteger(year) ||
      !Number.isInteger(month) ||
      !Number.isInteger(day)
    ) {
      throw new Error("Date components must be integers");
    }

    if (month < 1 || month > 12) {
      throw new Error("Month must be between 1 and 12");
    }

    const maxDay = new Date(year, month, 0).getDate();
    if (day < 1 || day > maxDay) {
      throw new Error(`Day must be between 1 and ${maxDay}`);
    }
  }

  /**
   * Validate BS date
   * @private
   */
  _validateBsDate(date) {
    if (!date || typeof date !== "object") {
      throw new Error("Invalid BS date format");
    }

    const { year, month, day } = date;

    if (
      !Number.isInteger(year) ||
      !Number.isInteger(month) ||
      !Number.isInteger(day)
    ) {
      throw new Error("Date components must be integers");
    }

    if (year < this.minBsYear || year > this.maxBsYear) {
      throw new Error(
        `BS year must be between ${this.minBsYear} and ${this.maxBsYear}`
      );
    }

    if (month < 1 || month > 12) {
      throw new Error("Month must be between 1 and 12");
    }

    const maxDay = this.bsMonthDays[year][month - 1];
    if (day < 1 || day > maxDay) {
      throw new Error(`Day must be between 1 and ${maxDay}`);
    }
  }

  /**
   * Get today's date in BS using the same method as in the original NepaliFunctions code
   */
  getTodayBS() {
    // Create a new Date object
    let now = new Date();

    // Set hours and minutes directly (Nepal timezone UTC+5:45)
    now.setHours(now.getHours() + 5);
    now.setMinutes(now.getMinutes() + 45);

    // Get date components in UTC
    let adDate = {
      year: now.getUTCFullYear(),
      month: now.getUTCMonth() + 1,
      day: now.getUTCDate(),
    };

    // Convert to BS
    console.log("AD Date:", adDate);
    console.log("BS Date:", this.adToBs(adDate));
    return this.adToBs(adDate);
  }

  /**
   * Get today's date in AD using the same method as in the original NepaliFunctions code
   */
  getTodayAD() {
    // Create a new Date object
    let now = new Date();

    // Set hours and minutes directly (Nepal timezone UTC+5:45)
    now.setHours(now.getHours() + 5);
    now.setMinutes(now.getMinutes() + 45);

    // Return date components in UTC
    return {
      year: now.getUTCFullYear(),
      month: now.getUTCMonth() + 1,
      day: now.getUTCDate(),
    };
  }

  /**
   * Get now in BS with proper Nepal timezone adjustment
   */
  getNowBS() {
    // Get today's date in BS
    const todayBS = this.getTodayBS();

    // Create a new Date object
    let now = new Date();

    // Set hours and minutes directly (Nepal timezone UTC+5:45)
    now.setHours(now.getHours() + 5);
    now.setMinutes(now.getMinutes() + 45);

    // Return a combined object with date from todayBS and time from now
    return {
      ...todayBS,
      hour: now.getUTCHours(),
      minute: now.getUTCMinutes(),
      second: now.getUTCSeconds(),
    };
  }

  /**
   * Get day of week for a BS date
   * @param {Object} bsDate - BS date object
   * @returns {number} - Day of week (0-6, where 0 is Sunday)
   */
  getDayOfWeek(bsDate) {
    // Convert BS to AD and then get day of week
    const adDate = this.bsToAd(bsDate);
    const jsDate = new Date(adDate.year, adDate.month - 1, adDate.day);

    // Return the day of week without any adjustment
    return jsDate.getDay();
  }

  /**
   *
   * Get the number of days in a specific month of a specific BS year
   * * @param {number} year - The BS year
   * @param {number} month - The BS month (1-12)
   * @returns {number} - The number of days in the specified month of the specified BS year
   * @throws {Error} - If the year or month is invalid
   * @example
   * const converter = new NepaliDateConverter();
   * const daysInMonth = converter.getDaysInMonth(2078, 2); // 29
   */
  getDaysInMonth(year, month) {
    if (!this.bsMonthDays[year] || !this.bsMonthDays[year][month - 1]) {
      throw new Error(`Invalid BS year ${year} or month ${month}`);
    }
    return this.bsMonthDays[year][month - 1];
  }

  /**
   * Get Nepali month names
   */
  getNepaliMonths() {
    return [
      "बैशाख",
      "जेठ",
      "असार",
      "श्रावण",
      "भदौ",
      "आश्विन",
      "कार्तिक",
      "मंसिर",
      "पौष",
      "माघ",
      "फाल्गुन",
      "चैत",
    ];
  }

  /**
   * Get English month names
   */
  getEnglishMonths() {
    return [
      "Baisakh",
      "Jestha",
      "Ashar",
      "Shrawan",
      "Bhadra",
      "Ashoj",
      "Kartik",
      "Mangsir",
      "Poush",
      "Magh",
      "Falgun",
      "Chaitra",
    ];
  }

  /**
   * Get Nepali weekday names
   */
  getNepaliWeekdays() {
    return [
      "आइतबार",
      "सोमबार",
      "मंगलबार",
      "बुधबार",
      "बिहीबार",
      "शुक्रबार",
      "शनिबार",
    ];
  }

  /**
   * Get English weekday names
   */
  getEnglishWeekdays() {
    return [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
  }

  /**
   * Convert number to Nepali digits
   */
  toNepaliDigits(number) {
    const nepaliDigits = ["०", "१", "२", "३", "४", "५", "६", "७", "८", "९"];
    return String(number).replace(/\d/g, (digit) => nepaliDigits[digit]);
  }

  /**
   * Format BS date
   */
  formatBsDate(date, format = "YYYY-MM-DD") {
    const year = String(date.year).padStart(4, "0");
    const month = String(date.month).padStart(2, "0");
    const day = String(date.day).padStart(2, "0");

    return format.replace("YYYY", year).replace("MM", month).replace("DD", day);
  }

  /**
   * Format AD date
   */
  formatAdDate(date, format = "YYYY-MM-DD") {
    const year = String(date.year).padStart(4, "0");
    const month = String(date.month).padStart(2, "0");
    const day = String(date.day).padStart(2, "0");

    return format.replace("YYYY", year).replace("MM", month).replace("DD", day);
  }

  /**
   * Get BS month days data
   * @private
   */
  _getBsMonthDays() {
    const data = {};

    // BS calendar data (1970-2100)
    data[1970] = [31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
    data[1971] = [31, 31, 32, 31, 32, 30, 30, 29, 30, 29, 30, 30];
    data[1972] = [31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 31];
    data[1973] = [30, 32, 31, 32, 31, 30, 30, 30, 29, 30, 29, 31];
    data[1974] = [31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
    data[1975] = [31, 31, 32, 32, 31, 30, 30, 29, 30, 29, 30, 30];
    data[1976] = [31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 31];
    data[1977] = [30, 32, 31, 32, 31, 31, 29, 30, 29, 30, 29, 31];
    data[1978] = [31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
    data[1979] = [31, 31, 32, 32, 31, 30, 30, 29, 30, 29, 30, 30];
    data[1980] = [31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 31];
    data[1981] = [31, 31, 31, 32, 31, 31, 29, 30, 30, 29, 30, 30];
    data[1982] = [31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
    data[1983] = [31, 31, 32, 32, 31, 30, 30, 29, 30, 29, 30, 30];
    data[1984] = [31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 31];
    data[1985] = [31, 31, 31, 32, 31, 31, 29, 30, 30, 29, 30, 30];
    data[1986] = [31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
    data[1987] = [31, 32, 31, 32, 31, 30, 30, 29, 30, 29, 30, 30];
    data[1988] = [31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 31];
    data[1989] = [31, 31, 31, 32, 31, 31, 30, 29, 30, 29, 30, 30];
    data[1990] = [31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
    data[1991] = [31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 30];
    data[1992] = [31, 32, 31, 32, 31, 30, 30, 30, 29, 30, 29, 31];
    data[1993] = [31, 31, 31, 32, 31, 31, 30, 29, 30, 29, 30, 30];
    data[1994] = [31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
    data[1995] = [31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 30];
    data[1996] = [31, 32, 31, 32, 31, 30, 30, 30, 29, 30, 29, 31];
    data[1997] = [31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
    data[1998] = [31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
    data[1999] = [31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 31];
    data[2000] = [30, 32, 31, 32, 31, 30, 30, 30, 29, 30, 29, 31];
    data[2001] = [31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
    data[2002] = [31, 31, 32, 32, 31, 30, 30, 29, 30, 29, 30, 30];
    data[2003] = [31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 31];
    data[2004] = [30, 32, 31, 32, 31, 30, 30, 30, 29, 30, 29, 31];
    data[2005] = [31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
    data[2006] = [31, 31, 32, 32, 31, 30, 30, 29, 30, 29, 30, 30];
    data[2007] = [31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 31];
    data[2008] = [31, 31, 31, 32, 31, 31, 29, 30, 30, 29, 29, 31];
    data[2009] = [31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
    data[2010] = [31, 31, 32, 32, 31, 30, 30, 29, 30, 29, 30, 30];
    data[2011] = [31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 31];
    data[2012] = [31, 31, 31, 32, 31, 31, 29, 30, 30, 29, 30, 30];
    data[2013] = [31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
    data[2014] = [31, 31, 32, 32, 31, 30, 30, 29, 30, 29, 30, 30];
    data[2015] = [31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 31];
    data[2016] = [31, 31, 31, 32, 31, 31, 29, 30, 30, 29, 30, 30];
    data[2017] = [31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
    data[2018] = [31, 32, 31, 32, 31, 30, 30, 29, 30, 29, 30, 30];
    data[2019] = [31, 32, 31, 32, 31, 30, 30, 30, 29, 30, 29, 31];
    data[2020] = [31, 31, 31, 32, 31, 31, 30, 29, 30, 29, 30, 30];
    data[2021] = [31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
    data[2022] = [31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 30];
    data[2023] = [31, 32, 31, 32, 31, 30, 30, 30, 29, 30, 29, 31];
    data[2024] = [31, 31, 31, 32, 31, 31, 30, 29, 30, 29, 30, 30];
    data[2025] = [31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
    data[2026] = [31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 31];
    data[2027] = [30, 32, 31, 32, 31, 30, 30, 30, 29, 30, 29, 31];
    data[2028] = [31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
    data[2029] = [31, 31, 32, 31, 32, 30, 30, 29, 30, 29, 30, 30];
    data[2030] = [31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 31];
    data[2031] = [30, 32, 31, 32, 31, 30, 30, 30, 29, 30, 29, 31];
    data[2032] = [31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
    data[2033] = [31, 31, 32, 32, 31, 30, 30, 29, 30, 29, 30, 30];
    data[2034] = [31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 31];
    data[2035] = [30, 32, 31, 32, 31, 31, 29, 30, 30, 29, 29, 31];
    data[2036] = [31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
    data[2037] = [31, 31, 32, 32, 31, 30, 30, 29, 30, 29, 30, 30];
    data[2038] = [31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 31];
    data[2039] = [31, 31, 31, 32, 31, 31, 29, 30, 30, 29, 30, 30];
    data[2040] = [31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
    data[2041] = [31, 31, 32, 32, 31, 30, 30, 29, 30, 29, 30, 30];
    data[2042] = [31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 31];
    data[2043] = [31, 31, 31, 32, 31, 31, 29, 30, 30, 29, 30, 30];
    data[2044] = [31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
    data[2045] = [31, 32, 31, 32, 31, 30, 30, 29, 30, 29, 30, 30];
    data[2046] = [31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 31];
    data[2047] = [31, 31, 31, 32, 31, 31, 30, 29, 30, 29, 30, 30];
    data[2048] = [31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
    data[2049] = [31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 30];
    data[2050] = [31, 32, 31, 32, 31, 30, 30, 30, 29, 30, 29, 31];
    data[2051] = [31, 31, 31, 32, 31, 31, 30, 29, 30, 29, 30, 30];
    data[2052] = [31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
    data[2053] = [31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 30];
    data[2054] = [31, 32, 31, 32, 31, 30, 30, 30, 29, 30, 29, 31];
    data[2055] = [31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
    data[2056] = [31, 31, 32, 31, 32, 30, 30, 29, 30, 29, 30, 30];
    data[2057] = [31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 31];
    data[2058] = [30, 32, 31, 32, 31, 30, 30, 30, 29, 30, 29, 31];
    data[2059] = [31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
    data[2060] = [31, 31, 32, 32, 31, 30, 30, 29, 30, 29, 30, 30];
    data[2061] = [31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 31];
    data[2062] = [30, 32, 31, 32, 31, 31, 29, 30, 29, 30, 29, 31];
    data[2063] = [31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
    data[2064] = [31, 31, 32, 32, 31, 30, 30, 29, 30, 29, 30, 30];
    data[2065] = [31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 31];
    data[2066] = [31, 31, 31, 32, 31, 31, 29, 30, 30, 29, 29, 31];
    data[2067] = [31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
    data[2068] = [31, 31, 32, 32, 31, 30, 30, 29, 30, 29, 30, 30];
    data[2069] = [31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 31];
    data[2070] = [31, 31, 31, 32, 31, 31, 29, 30, 30, 29, 30, 30];
    data[2071] = [31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
    data[2072] = [31, 32, 31, 32, 31, 30, 30, 29, 30, 29, 30, 30];
    data[2073] = [31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 31];
    data[2074] = [31, 31, 31, 32, 31, 31, 30, 29, 30, 29, 30, 30];
    data[2075] = [31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
    data[2076] = [31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 30];
    data[2077] = [31, 32, 31, 32, 31, 30, 30, 30, 29, 30, 29, 31];
    data[2078] = [31, 31, 31, 32, 31, 31, 30, 29, 30, 29, 30, 30];
    data[2079] = [31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
    data[2080] = [31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 30];
    data[2081] = [31, 32, 31, 32, 31, 30, 30, 30, 29, 30, 29, 31];
    data[2082] = [31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
    data[2083] = [31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
    data[2084] = [31, 31, 32, 31, 31, 30, 30, 30, 29, 30, 30, 30];
    data[2085] = [31, 32, 31, 32, 30, 31, 30, 30, 29, 30, 30, 30];
    data[2086] = [30, 32, 31, 32, 31, 30, 30, 30, 29, 30, 30, 30];
    data[2087] = [31, 31, 32, 31, 31, 31, 30, 30, 29, 30, 30, 30];
    data[2088] = [30, 31, 32, 32, 30, 31, 30, 30, 29, 30, 30, 30];
    data[2089] = [30, 32, 31, 32, 31, 30, 30, 30, 29, 30, 30, 30];
    data[2090] = [30, 32, 31, 32, 31, 30, 30, 30, 29, 30, 30, 30];
    data[2091] = [31, 31, 32, 31, 31, 31, 30, 30, 29, 30, 30, 30];
    data[2092] = [30, 31, 32, 32, 31, 30, 30, 30, 29, 30, 30, 30];
    data[2093] = [30, 32, 31, 32, 31, 30, 30, 30, 29, 30, 30, 30];
    data[2094] = [31, 31, 32, 31, 31, 30, 30, 30, 29, 30, 30, 30];
    data[2095] = [31, 31, 32, 31, 31, 31, 30, 29, 30, 30, 30, 30];
    data[2096] = [30, 31, 32, 32, 31, 30, 30, 29, 30, 29, 30, 30];
    data[2097] = [31, 32, 31, 32, 31, 30, 30, 30, 29, 30, 30, 30];
    data[2098] = [31, 31, 32, 31, 31, 31, 29, 30, 29, 30, 29, 31];
    data[2099] = [31, 31, 32, 31, 31, 31, 30, 29, 29, 30, 30, 30];

    return data;
  }
}

export default NepaliDateConverter;
